import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/text.default.js';

const VALID_VARIANTS = ['display', 'heading', 'title', 'body', 'caption', 'label', 'code'];
const VALID_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
const VALID_WEIGHTS = ['regular', 'medium', 'semibold', 'bold'];
const VALID_ALIGNS = ['start', 'center', 'end'];

class RunText extends skinResolutionMixin(RunElement) {
  #variant = 'body';
  #size = 'md';
  #weight = 'regular';
  #align = 'start';
  #truncate = false;
  #color = '';

  static defaultSkin = defaultSkin;

  static skinLoaders = {
    wireframe: () => import('./skins/text.wireframe.js'),
  };

  static get observedAttributes() {
    return ['variant', 'size', 'weight', 'align', 'truncate', 'color'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'variant':  this.variant  = newValue; break;
      case 'size':     this.size     = newValue; break;
      case 'weight':   this.weight   = newValue; break;
      case 'align':    this.align    = newValue; break;
      case 'truncate': this.truncate = newValue !== null; break;
      case 'color':    this.color    = newValue; break;
    }
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const coerced = VALID_VARIANTS.includes(String(value).trim())
      ? String(value).trim()
      : 'body';
    if (this.#variant === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get size() { return this.#size; }
  set size(value) {
    const coerced = VALID_SIZES.includes(String(value).trim())
      ? String(value).trim()
      : 'md';
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  get weight() { return this.#weight; }
  set weight(value) {
    const coerced = VALID_WEIGHTS.includes(String(value).trim())
      ? String(value).trim()
      : 'regular';
    if (this.#weight === coerced) return;
    this.#weight = coerced;
    this.setAttribute('weight', coerced);
    this.requestUpdate();
  }

  get align() { return this.#align; }
  set align(value) {
    const coerced = VALID_ALIGNS.includes(String(value).trim())
      ? String(value).trim()
      : 'start';
    if (this.#align === coerced) return;
    this.#align = coerced;
    this.setAttribute('align', coerced);
    this.requestUpdate();
  }

  get truncate() { return this.#truncate; }
  set truncate(value) {
    const coerced = Boolean(value);
    if (this.#truncate === coerced) return;
    this.#truncate = coerced;
    this.toggleAttribute('truncate', coerced);
    this.requestUpdate();
  }

  get color() { return this.#color; }
  set color(value) {
    const coerced = value == null ? '' : String(value).trim();
    if (this.#color === coerced) return;
    this.#color = coerced;
    if (coerced) {
      this.setAttribute('color', coerced);
    } else {
      this.removeAttribute('color');
    }
    this.requestUpdate();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  #updateAria() {
    if (this.#variant === 'heading' || this.#variant === 'display' || this.#variant === 'title') {
      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'heading');
      }
    } else {
      if (this.getAttribute('role') === 'heading') {
        this.removeAttribute('role');
      }
    }
  }
}

if (!customElements.get('run-text')) {
  customElements.define('run-text', RunText);
}

export { RunText };
export default RunText;